import { Injectable, SecurityContext } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import Swal, { SweetAlertOptions } from "sweetalert2";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root"
})
export class MessageService {
  constructor(
    readonly translateService: TranslateService,
    private readonly sanitizer: DomSanitizer
  ) {}

  fire(args: SweetAlertOptions) {
    return Swal.fire(args);
  }

  something(errorText) {
    return Swal.fire({
      icon: "error",
      title: this.translateService.instant("message.Oops"),
      text: errorText
    });
  }

  deleteError(titleText, errorText) {
    return Swal.fire({
      icon: "error",
      title: titleText,
      text: errorText
    });
  }

  deleteWarn(objetName) {
    const safeName = this.sanitizer.sanitize(SecurityContext.HTML, objetName) ?? "";
    return Swal.fire({
      title: this.translateService.instant("message.Are_you_sure"),
      html: `${this.translateService.instant("message.This_will_permanently_delete_")} ${safeName}
      ${this.translateService.instant("message._and_all_of_its_data")}<br><span>
      ${this.translateService.instant("message.Write")}<b style='font-weight: 700'>${this.translateService.instant("message.DELETE")}</b>
      ${this.translateService.instant("message.to_confirm")}</span>`,
      icon: "warning",
      input: "text",
      inputValidator: value => {
        if (value !== this.translateService.instant("message.DELETE")) {
          return this.translateService.instant("message.You_need_to_write_DELETE_in_uppercase");
        }
      },
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Yes_delete_it"),
      cancelButtonText: this.translateService.instant("message.No_keep_it")
    });
  }

  simpleDelete() {
    return Swal.fire({
      title: this.translateService.instant("message.Are_you_sure"),
      text: this.translateService.instant("message.You_Want_to_delete"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Yes_delete_it"),
      cancelButtonText: this.translateService.instant("message.No_keep_it")
    });
  }

  removeLabelWarning(dynamicMessage: string) {
    return Swal.fire({
      title: this.translateService.instant("message.Are_you_sure"),
      text: dynamicMessage || this.translateService.instant("message.You_Want_to_remove"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Yes_delete_it"),
      cancelButtonText: this.translateService.instant("message.No_keep_it")
    });
  }

  archiveConfirmation(confirmationTitle: string, confirmationText: string) {
    return Swal.fire({
      title: this.translateService.instant(confirmationTitle),
      text: this.translateService.instant(confirmationText),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Yes_Continue"),
      cancelButtonText: this.translateService.instant("message.No_Cancel")
    });
  }

  deleteSuccess(moduleName) {
    let translatedParam;
    if (!/\d/.test(moduleName)) {
      translatedParam = this.translateParams(moduleName);
    } else {
      translatedParam = moduleName;
    }
    return Swal.fire(
      this.translateService.instant("message.Deleted"),
      `${translatedParam} ${this.translateService.instant("message.Deleted_Successfully")}`,
      "success"
    );
  }

  updateSuccess(moduleName) {
    const translatedParam = this.translateParams(moduleName);
    return Swal.fire(
      this.translateService.instant("message.Updated"),
      `${translatedParam} ${this.translateService.instant("message.Updated_Successfully")}`,
      "success"
    );
  }

  createSuccess(moduleName) {
    const translatedParam = this.translateParams(moduleName);
    return Swal.fire(
      this.translateService.instant("message.Created"),
      `${translatedParam} ${this.translateService.instant("message.Created_Successfully")}`,
      "success"
    );
  }

  confirmDownload() {
    return Swal.fire({
      title: this.translateService.instant("message.Company_has_not_filled_document_completely"),
      html: this.translateService.instant("message.Do_you_still_want_to_download_it"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Download"),
      cancelButtonText: this.translateService.instant("message.Close")
    });
  }

  confirmPreview() {
    return Swal.fire({
      title: this.translateService.instant("message.Company_has_not_filled_document_completely"),
      html: this.translateService.instant("message.Do_you_still_want_to_preview_it"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Preview"),
      cancelButtonText: this.translateService.instant("message.Close")
    });
  }

  downloadSuccess(moduleName) {
    return Swal.fire(
      this.translateService.instant("message.Downloaded"),
      `${moduleName} ${this.translateService.instant("message.Downloaded")}`,
      "success"
    );
  }

  confirmMultipleDocumentsDownload() {
    return Swal.fire({
      title: this.translateService.instant("message.Multiple_documents_available"),
      html: this.translateService.instant("message.Do_you_want_to_download"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Download"),
      cancelButtonText: this.translateService.instant("message.Close")
    });
  }

  comingSoon() {
    return Swal.fire({
      title: this.translateService.instant("message.Coming_Soon")
      // text: 'Coming Soon',
      // imageUrl: 'https://assets.stickpng.com/images/58889577bc2fc2ef3a1860be.png',
      // imageWidth: 400,
      // imageHeight: 200,
      // imageAlt: 'Custom image',
    });
  }

  translateParams(param) {
    return this.translateService.instant("message." + param);
  }

  warningConfirmation(title) {
    return Swal.fire({
      title: this.translateService.instant(title),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Continue"),
      cancelButtonText: this.translateService.instant("message.Cancel")
    });
  }

  unlinkRelationConfirmation(confirmationTitle, confirmationText) {
    return Swal.fire({
      title: this.translateService.instant(confirmationTitle),
      text: this.translateService.instant(confirmationText),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.translateService.instant("message.Yes_Continue"),
      cancelButtonText: this.translateService.instant("message.No_Cancel")
    });
  }

  ArchivedRelationConfirmation() {
    const title = this.translateService.instant("form-area.Unarchive_to_add_as_relation");
    const description = this.translateService.instant("form-area.This_relation_is_archived_unarchive_them_to_link_them_as_a_relation");
    const confirmText = this.translateService.instant("form-area.Unarchive_and_link_customer");
    const cancelText = this.translateService.instant("message.Cancel");

    return Swal.fire({
      html: `
        <div class="flex flex-col items-center text-center text-[#05323b]">
          <div class="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[#0c6175] text-white">
            <img src="assets/images/archive-restore-fill.svg" width="24" height="24" alt="" />
          </div>
          <h2 class="m-0 mb-2 max-w-full text-center text-[20px] font-bold leading-[27px] text-[#05323b]">${title}</h2>
          <p class="m-0 max-w-[300px] text-sm font-normal leading-5 text-[#1e2229]">${description}</p>
        </div>
      `,
      showCancelButton: true,
      buttonsStyling: false,
      confirmButtonText: `
        <span class="inline-flex h-full items-center justify-center gap-1.5 whitespace-nowrap leading-none">
          <img src="assets/images/archive-restore.svg" width="18" height="18" alt="" />
          <span>${confirmText}</span>
        </span>
      `,
      cancelButtonText: cancelText,
      customClass: {
        popup: "archived-relation-modal",
        htmlContainer: "archived-relation-modal__html",
        actions: "archived-relation-modal__actions",
        confirmButton: "archived-relation-modal__confirm",
        cancelButton: "archived-relation-modal__cancel"
      }
    });
  }

  InfoWarning(title, text, buttonText = "message.Got_it") {
    return Swal.fire({
      title: this.translateService.instant(title),
      text: this.translateService.instant(text),
      icon: "info",
      confirmButtonText: this.translateService.instant(buttonText)
    });
  }
}
